"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Plus, Globe, Users, LogOut, LogIn, Eye, EyeOff, Star, Search } from "lucide-react";
import { DiscordLogoIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ClanDetailDialog } from "@/components/section-clans/clan-detail-dialog";
import type { Clan } from "@/lib/types";
import { createClan, joinClan, leaveClan } from "@/query/clans";

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300",
  "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/60 dark:text-cyan-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/60 dark:text-pink-300",
];

function clanAvatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export function ClanAvatar({ name, size = "md" }: { name: string; size?: "sm" | "md" | "lg" }) {
  const initials =
    name
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 2)
      .toUpperCase() || "??";
  const color = clanAvatarColor(name);
  const sz = size === "lg" ? "h-12 w-12 text-base" : size === "sm" ? "h-7 w-7 text-xs" : "h-10 w-10 text-sm";
  return <div className={`${sz} ${color} rounded-xl flex items-center justify-center font-bold shrink-0 select-none`}>{initials}</div>;
}

export const DISCORD_REGEX = /^https?:\/\/(discord\.gg|discord\.com\/invite|discordapp\.com\/invite)\/[a-zA-Z0-9]+$/;

function isValidDiscordUrl(url: string) {
  return url === "" || DISCORD_REGEX.test(url.trim());
}

type Props = {
  clans: Clan[];
  isLoggedIn: boolean;
  userClan: string | null;
};

export function ClanMemberView({ clans, isLoggedIn, userClan }: Props) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState<Clan | null>(null);
  const [detailClan, setDetailClan] = useState<Clan | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showJoinPassword, setShowJoinPassword] = useState(false);

  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [discord, setDiscord] = useState("");
  const [password, setPassword] = useState("");
  const [joinPassword, setJoinPassword] = useState("");

  function resetCreateForm() {
    setName("");
    setTag("");
    setDescription("");
    setWebsite("");
    setDiscord("");
    setPassword("");
    setShowPassword(false);
  }

  function handleCreate() {
    startTransition(async () => {
      const result = await createClan({ name, tag, description, website, discord, password });
      if (result.success) {
        toast.success(`Clan "${name}" wurde erstellt. Du bist automatisch beigetreten.`);
        setCreateOpen(false);
        resetCreateForm();
        router.refresh();
      } else if (result.reason === "name_exists") {
        toast.error("Ein Clan mit diesem Namen existiert bereits.");
      } else if (result.reason === "tag_exists") {
        toast.error("Dieser Clan-Tag wird bereits verwendet.");
      } else if (result.reason === "empty_tag") {
        toast.error("Bitte vergib einen Clan-Tag.");
      } else if (result.reason === "empty_password") {
        toast.error("Bitte vergib ein Passwort für den Clan.");
      } else {
        toast.error("Fehler beim Erstellen des Clans.");
      }
    });
  }

  function handleJoin(clan: Clan) {
    startTransition(async () => {
      const result = await joinClan(clan.id, joinPassword);
      if (result.success) {
        toast.success(`Du bist dem Clan "${clan.name}" beigetreten.`);
        setJoinOpen(null);
        setJoinPassword("");
        setShowJoinPassword(false);
        router.refresh();
      } else if (result.reason === "wrong_password") {
        toast.error("Falsches Passwort.");
      } else {
        toast.error("Fehler beim Beitreten.");
      }
    });
  }

  function handleLeave() {
    startTransition(async () => {
      const result = await leaveClan();
      if (result.clanDeleted) {
        toast.success(`Du hast „${userClan}" verlassen. Da du das letzte Mitglied warst, wurde der Clan gelöscht.`);
      } else {
        toast.success("Du hast den Clan verlassen.");
      }
      router.refresh();
    });
  }

  const filtered = search.trim() ? clans.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase())) : clans;

  const myClanMemberCount = userClan ? (clans.find((c) => c.name === userClan)?.member_count ?? 0) : 0;
  const isLastMemberOfMyClan = myClanMemberCount <= 1;

  return (
    <>
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              Alle Clans
              <span className="ml-2 text-sm font-normal text-muted-foreground">({clans.length})</span>
            </CardTitle>
            {isLoggedIn && (
              <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
                <Plus className="h-4 w-4" /> Clan erstellen
              </Button>
            )}
          </CardHeader>

          {/* Search */}
          {clans.length > 0 && (
            <div className="px-6 pb-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Clans durchsuchen…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
              </div>
            </div>
          )}

          <CardContent className="p-0">
            {clans.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">Noch keine Clans vorhanden.</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">Kein Clan gefunden.</p>
            ) : (
              <ul>
                {filtered.map((c, i) => {
                  const isMyCurrentClan = c.name === userClan;
                  const count = c.member_count ?? 0;
                  return (
                    <li key={c.id}>
                      {i > 0 && <Separator />}
                      <div className={`flex items-center gap-4 px-4 py-2 transition-colors `}>
                        {/* Clickable: Avatar + Info → opens detail */}
                        <button className={`p-1 rounded-xl border border-border flex items-center gap-4 min-w-0 flex-1 text-left hover:bg-sidebar-accent ${isMyCurrentClan ? "bg-primary/50" : ""}`} onClick={() => setDetailClan(c)}>
                          <ClanAvatar name={c.name} size="md" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold leading-tight hover:underline underline-offset-2">{c.name}</span>
                              {c.tag && <span className="text-xs font-mono font-semibold text-muted-foreground">[{c.tag}]</span>}
                              {isMyCurrentClan && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium primary">
                                  <Star className="h-3 w-3 fill-current" /> Mein Clan
                                </span>
                              )}
                            </div>
                            {c.description && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{c.description}</p>}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Users className="h-3.5 w-3.5" />
                                {count} {count === 1 ? "Mitglied" : "Mitglieder"}
                              </span>
                              {c.website && (
                                <a href={c.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-blue-500 transition-colors">
                                  <Globe className="h-3.5 w-3.5" /> Webseite
                                </a>
                              )}
                              {c.discord && (
                                <a href={c.discord} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-indigo-500 transition-colors">
                                  <DiscordLogoIcon className="h-3.5 w-3.5" /> Discord
                                </a>
                              )}
                            </div>
                          </div>
                        </button>

                        {/* Action buttons */}
                        {isLoggedIn &&
                          (isMyCurrentClan ? (
                            isLastMemberOfMyClan ? (
                              <ConfirmDialog
                                trigger={
                                  <Button size="sm" variant="outline" className="gap-1.5 shrink-0" disabled={isPending}>
                                    <LogOut className="h-3.5 w-3.5" /> Verlassen
                                  </Button>
                                }
                                title={`Clan „${c.name}" verlassen?`}
                                description="Du bist das letzte Mitglied. Wenn du den Clan verlässt, wird er dabei gelöscht."
                                confirmLabel="Verlassen und löschen"
                                onConfirm={handleLeave}
                              />
                            ) : (
                              <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={handleLeave} disabled={isPending}>
                                <LogOut className="h-3.5 w-3.5" /> Verlassen
                              </Button>
                            )
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1.5 shrink-0"
                              onClick={() => {
                                setJoinOpen(c);
                                setJoinPassword("");
                                setShowJoinPassword(false);
                              }}
                            >
                              <LogIn className="h-3.5 w-3.5" /> Beitreten
                            </Button>
                          ))}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail dialog */}
      <ClanDetailDialog clan={detailClan} isLoggedIn={isLoggedIn} onClose={() => setDetailClan(null)} />

      {/* Create clan dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            resetCreateForm();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Neuen Clan erstellen</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div>
              <Label htmlFor="clan-name">Clan-Name</Label>
              <Input id="clan-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. BK-LAN e.V." maxLength={100} />
            </div>
            <div>
              <Label htmlFor="clan-tag">
                Clan-Tag <span className="text-muted-foreground text-xs">(wird als [TAG] angezeigt, max. 3 Zeichen)</span>
              </Label>
              <Input id="clan-tag" value={tag} onChange={(e) => setTag(e.target.value.toUpperCase())} placeholder="z. B. BKL" maxLength={3} className="uppercase" required />
            </div>
            <div>
              <Label htmlFor="clan-password">
                Passwort <span className="text-muted-foreground text-xs">(zum Beitreten)</span>
              </Label>
              <div className="relative">
                <Input id="clan-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Clan-Passwort" className="pr-9" />
                <button type="button" onClick={() => setShowPassword((p) => !p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="clan-desc">
                Beschreibung <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea id="clan-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Kurze Beschreibung..." rows={3} />
            </div>
            <div>
              <Label htmlFor="clan-website">
                Webseite <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input id="clan-website" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <Label htmlFor="clan-discord">
                Discord <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input id="clan-discord" value={discord} onChange={(e) => setDiscord(e.target.value)} placeholder="https://discord.gg/…" className={discord && !isValidDiscordUrl(discord) ? "border-red-400 focus-visible:ring-red-400" : ""} />
              {discord && !isValidDiscordUrl(discord) && <p className="text-xs text-red-500 mt-1">Ungültiger Discord-Link. Erlaubt: discord.gg/… oder discord.com/invite/…</p>}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateOpen(false);
                  resetCreateForm();
                }}
                disabled={isPending}
              >
                Abbrechen
              </Button>
              <Button onClick={handleCreate} disabled={isPending || !name.trim() || !tag.trim() || !password.trim() || (!!discord && !isValidDiscordUrl(discord))}>
                Erstellen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Join clan dialog */}
      <Dialog
        open={!!joinOpen}
        onOpenChange={(o) => {
          if (!o) {
            setJoinOpen(null);
            setJoinPassword("");
            setShowJoinPassword(false);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Clan beitreten: {joinOpen?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            {userClan && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Du verlässt dabei deinen aktuellen Clan „{userClan}".{isLastMemberOfMyClan && " Da du das letzte Mitglied bist, wird er dabei gelöscht."}
              </p>
            )}
            <div>
              <Label htmlFor="join-password">Clan-Passwort</Label>
              <div className="relative">
                <Input
                  id="join-password"
                  type={showJoinPassword ? "text" : "password"}
                  value={joinPassword}
                  onChange={(e) => setJoinPassword(e.target.value)}
                  placeholder="Passwort eingeben"
                  className="pr-9"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && joinOpen && joinPassword.trim()) handleJoin(joinOpen);
                  }}
                  autoFocus
                />
                <button type="button" onClick={() => setShowJoinPassword((p) => !p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showJoinPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => {
                  setJoinOpen(null);
                  setJoinPassword("");
                }}
                disabled={isPending}
              >
                Abbrechen
              </Button>
              <Button onClick={() => joinOpen && handleJoin(joinOpen)} disabled={isPending || !joinPassword.trim()}>
                Beitreten
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
